/**
 * Motion and tactile feedback.
 *
 * Every effect here is optional and every one degrades to nothing: haptics
 * are absent on desktop and on iOS Safari, and reduced-motion is a user
 * preference worth honouring rather than overriding.
 */

const motionQuery =
  typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

export function reducedMotion(): boolean {
  return motionQuery?.matches ?? false;
}

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, reducedMotion() ? Math.min(ms, 1) : ms));
}

export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/** Resolve once an element's running animations and transitions have settled. */
export function settled(node: Element): Promise<void> {
  return Promise.all([...node.getAnimations({ subtree: true })].map((a) => a.finished.catch(() => {})))
    .then(() => undefined);
}

type HapticPattern = 'tap' | 'select' | 'blend' | 'undo' | 'win' | 'miss' | 'spend';

const PATTERNS: Record<HapticPattern, number | number[]> = {
  tap: 8,
  select: 12,
  blend: [10, 26, 14],
  undo: [8, 20, 8],
  spend: 6,
  win: [14, 40, 14, 40, 26],
  miss: [30, 50, 12],
};

export function haptic(pattern: HapticPattern = 'tap'): void {
  const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  try {
    nav.vibrate?.(PATTERNS[pattern]);
  } catch {
    /* some browsers throw when the tab is backgrounded */
  }
}

/**
 * Run a CSS animation class and take it off again afterwards.
 *
 * The class has to be removed *and* the node's animation restarted, or
 * retriggering the same effect on the same element is a no-op.
 */
export function flash(node: HTMLElement, className: string, ms: number): void {
  node.classList.remove(className);
  void node.offsetWidth;
  node.classList.add(className);
  setTimeout(() => node.classList.remove(className), ms);
}

/** Count a number up on screen. Cheap, and it makes a score feel earned. */
export function countUp(node: HTMLElement, to: number, ms = 700, decimals = 0): () => void {
  if (reducedMotion()) {
    node.textContent = to.toFixed(decimals);
    return () => {};
  }
  let raf = 0;
  const start = performance.now();
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / ms);
    const eased = 1 - Math.pow(1 - t, 3);
    node.textContent = (to * eased).toFixed(decimals);
    if (t < 1) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}
